import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { OverlayProps } from "./types";
import { Button } from "./Button";

const Overlay: React.FC<OverlayProps> = ({ 
    title, 
    children, 
    isOpen, 
    onClose,
    paragraphWithCopyButton,
    buttons
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleCopy = (copyValue: string, buttonElement: HTMLButtonElement) => {
    navigator.clipboard.writeText(copyValue);
    // Temporarily change the button's background color
    buttonElement.classList.add("bg-accent");

    // Revert the background color after 500ms
    setTimeout(() => {
      buttonElement.classList.remove("bg-accent");
    }, 100);
  };

  const maxWidthSmallScreens = 768;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
        {window.innerWidth < maxWidthSmallScreens && (
          /* Background Overlay (Fades In) - only on small screens */
          <motion.div id="obfuscatedBackground"
            className="fixed inset-0 bg-background-70 pointer-events-auto z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            onClick={onClose}
          />
        )}
          
          {/* Bottom Sheet Overlay (Slides In, Now Centered) - remove justify-between*/}
          <motion.div id="overlaySheetWithGap"
            className="fixed bottom-0 left-0 right-0 mx-auto 
                      w-full max-w-[500px] flex flex-col z-[101]
                      border-t-[50px] border-b-0 border-r-0 border-l-0 border-transparent 
                      h-full max-h-screen overflow-y-auto custom-scrollbar
                      md:right-auto md:w-small md:border-t-header-desktop"
            initial={{ 
              y: window.innerWidth < maxWidthSmallScreens ? "100%" : 0,      // Slide in from bottom only on small screens
              x: window.innerWidth < maxWidthSmallScreens ? 0 : -450 }}    // Slide in from left on larger screens
            animate={{ y: 0, x: 0 }}
            exit={{ 
              y: window.innerWidth < maxWidthSmallScreens ? "100%" : 0,       // Slide out to bottom only on small screens
              x: window.innerWidth < maxWidthSmallScreens ? 0 : -450 }}    // Slide in from left on larger screens
            transition={{ 
              type: "tween", 
              duration: 0.3 }}
            onClick={onClose}
          >

             {/* Fixed Header Section */}
            <div id="overlayHeader" 
                 className="rounded-t-lg bg-background w-full pl-[16px] md:pl-[24px] pt-[16px] md:pt-[24px] shadow-lg flex items-center justify-between sticky top-0 z-[102]
                 md:rounded-none">
              {/* Title (added flex-1) */}
              <h2 className="text-lg font-bold text-left flex-1">{title}</h2>

              {/* Close Button - removed absolute top-4 right-4 */}
              <button
                className="text-xl pointer-events-auto w-[40px] h-[40px] mr-[8px] md:mr-[12px] flex items-center justify-center"
                onClick={onClose}
                aria-label="Close">
                <img
                  src="/assets/img/buttonClose.svg"
                  alt="Close"
                  className="w-[12px] h-[12px] object-contain"
                />
              </button>
            </div>

            {/* Scrollable Content Section - removed rounded-t-lg and overflow-y-auto */}
            <div id="overlaySheet" 
              className="bg-background w-full px-[16px] md:px-[24px] shadow-lg flex-grow tracking-normal"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 32px)" }}
              onClick={(e) => e.stopPropagation()}
            >

              {children && (
                  <div className="text-sm md:text-md text-left">{children}</div>
              )}

              {/* Paragraph with Copy Button */}
              {paragraphWithCopyButton && (
                <div className="flex justify-between items-center mt-[24px] md:mt-[32px] text-sm md:text-md text-left gap-[16px] md:gap-[24px]">
                  <div className="flex flex-col gap-1">{paragraphWithCopyButton.paragraph}</div>
                  <button id="Copy (button)"
                    onClick={(e) => handleCopy(paragraphWithCopyButton.copyValue, e.currentTarget as HTMLButtonElement)}
                    className="px-[8px] py-[6px] rounded-sm border border-solid border-tertiary w-auto font-semibold"
                    >
                      <span id="Copy (button)" className="text-sm md:text-md tracking-tight">Copy</span>
                  </button>
                </div>
              )}

              {/* Buttons */}
              {buttons && (
                <div className="space-y-3 mt-[24px] md:mt-[32px]">
                  {buttons.map((button, index) => (
                    <Button
                      key={index}
                      buttonKey={index}
                      type="function"
                      button={button.text}
                      onClick={button.onClick}
                      {...(button.bgColor && { bgColor: button.bgColor })}
                      {...(button.buttonExtraClasses && { buttonExtraClasses: button.buttonExtraClasses })}
                      {...(button.image && { image: button.image })}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Overlay;